import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os cadastros de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/mais-acesso-aps/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros por microárea
 *
 * @param {*} cod_ibge
 * @param {*} nu_ine
 * @param {*} micro_area
 *
 * @returns {Array}
 */
export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/mais-acesso-aps/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna o nome da cor de acordo com o valor
 * @param {Number} valor - Valor numérico
 * @returns {String}
 */
export function getCorTextoResultado(valor) {
  if (valor >= 60) {
    return 'green-color'
  } else {
    return 'red-color'
  }
}

/**
 * Regra para pintar a cor da linha da tabela
 *
 * @param {*} item
 * @returns {String}
 */
export function printTableRow(item) {
  if (item.num_mais_acesso >= 1) {
    return 'green'
  } else {
    return 'orange'
  }
}

/**
 * Retorna a quantidade de agendados.
 *
 * Se houver mês selecionado, conta apenas os registros do período.
 * Caso contrário, conta todos os registros com nm igual a 1.
 *
 * @param {Object} item Cadastro.
 * @param {Object|null} mes Mês/período selecionado.
 * @returns {Number} Quantidade de agendados.
 */
export function getQuantidadeAgendados(item, mes = null) {
  let dados = []

  try {
    dados =
      typeof item.dados === 'string' ? JSON.parse(item.dados) : item.dados || []
  } catch (e) {
    dados = []
  }

  return dados.filter((proc) => {
    if (Number(proc.nm) === 0) {
      return false
    }

    if (!mes) {
      return true
    }

    const mesesSelecionados = mes.value || []
    const anoSelecionado = Number(mes.nu_ano)

    return (
      mesesSelecionados.includes(Number(proc.nu_mes)) &&
      Number(proc.nu_ano) === anoSelecionado
    )
  }).length
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 * @param {Object|String|Number|null} mes Mês selecionado no filtro.
 */
export function generatePdf(cadastros, selectEquipe, microArea, mes = null) {
  let title_pdf = 'Mais acesso'
  let titlePdfSave = utils.methods.titlePdf('Mais acesso')

  let description_pdf =
    'Mais Acesso à APS. Demanda programada vs demanda espontânea'
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', 'AGENDADOS']
  var data_pdf = []
  for (let i = 0; i < cadastros.length; i++) {
    var obj = {}

    let dados = []

    try {
      dados = JSON.parse(cadastros[i].dados ?? '[]')
    } catch (e) {
      dados = []
    }

    obj.no_cidadao = cadastros[i].no_cidadao
    obj.dt_nascimento =
      utils.methods.formatDate(cadastros[i].dt_nascimento) +
      `\n(${cadastros[i].idade} ${cadastros[i].idade > 1 ? 'anos' : 'ano'})`
    obj.no_mae = cadastros[i].no_mae
    obj.agendados = getQuantidadeAgendados(cadastros[i], mes)
    obj.num_mais_acesso = cadastros[i].num_mais_acesso
    data_pdf.push(obj)
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      const dadosCabecalho = []

      const nomeMes = utils.methods.getDescricaoMes(mes)

      if (nomeMes) {
        dadosCabecalho.push({
          label: 'Mês',
          value: nomeMes,
        })
      }

      dadosCabecalho.push(
        {
          label: 'Equipe',
          value: selectEquipe.no_equipe,
        },
        {
          label: 'CNES',
          value: selectEquipe.nu_cnes,
        },
        {
          label: 'INE',
          value: selectEquipe.nu_ine,
        },
        {
          label: 'Microárea',
          value: microArea,
        }
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        dadosCabecalho,
        verticalPosition,
        'l'
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          let num_mais_acesso = data.row.raw[4]
          if (num_mais_acesso >= 1) {
            doc.setTextColor(2, 99, 57) // verde
          } else {
            doc.setTextColor(255, 165, 0) // laranja
          }
          // pinta a cor da descrição das colunas de preto
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

function getMesAnoAtual() {
  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())

  return `${mesAtual}/${anoAtual}`
}

function montarResultado(dados, tipoResultado = 'quadrimestral') {
  if (tipoResultado === 'mensal') {
    return {
      numerador: dados.numerador_mes,
      denominador: dados.denominador_mes,
      resultado: dados.resultado_mes,
      classificacao: dados.classificacao_mes,
    }
  }

  return {
    numerador: dados.numerador,
    denominador: dados.denominador,
    resultado: dados.resultado,
    classificacao: dados.classificacao,
  }
}

export function percentualMunicipio(
  dadosDaTabelaResultado,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 46 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDoMunicipio) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDoMunicipio.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualEquipe(
  dadosDaTabelaResultado,
  nuIne,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 46 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaEquipe) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaEquipe.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 46 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaMicroArea) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaMicroArea.dados)

  return montarResultado(dados, tipoResultado)
}
